/* eslint-disable react/no-multi-comp -- техдолг: вспомогательные компоненты вынести в отдельные файлы (docs/todos/tech-debt/admin-page-helper-components.md) */
import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import type { CourseLevel } from '@cookspace-shared-contracts/core';
import { ArrowLeft, Check, ChevronDown, ChevronRight, Eye, X } from 'lucide-react';
import remarkGfm from 'remark-gfm';

import {
    useApproveContentMutation,
    useGetModerationArticleQuery,
    useGetModerationCourseQuery,
    useGetModerationEventQuery,
    useGetModerationRecipeQuery,
    useGetModerationStreamQuery,
    useRejectContentMutation
} from '@/api/moderationApi';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import type { ModerationContentType } from '@/lib/moderationSchemas';

const COURSE_LEVEL_LABEL: Record<CourseLevel, string> = {
    beginner: 'Начинающий',
    intermediate: 'Средний',
    advanced: 'Продвинутый'
};

const REASON_MIN_LENGTH = 10;

interface RecipeViewProps {
    id: string;
}

function RecipeView({ id }: RecipeViewProps): ReactElement {
    const { data, isLoading, isError } = useGetModerationRecipeQuery({ id });

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка рецепта...</div>;
    }
    if (isError || !data) {
        return (
            <div className="text-sm text-red-600" data-testid="moderation-detail-error">
                Не удалось загрузить рецепт
            </div>
        );
    }

    const { recipe } = data;

    return (
        <div className="space-y-4" data-testid="moderation-recipe-view">
            <div>
                <h2 className="text-2xl font-semibold text-gray-900">{recipe.title}</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Автор: {recipe.authorDisplayName} · Сложность: {recipe.difficulty} · Порций:{' '}
                    {recipe.servings} · Время: {recipe.cookingTimeMin} мин
                </p>
            </div>

            {recipe.coverUrl !== null && recipe.coverUrl !== '' && (
                <img
                    src={recipe.coverUrl}
                    alt=""
                    className="max-h-80 w-full rounded object-cover"
                />
            )}

            <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                {recipe.description}
            </div>

            <section>
                <h3 className="mb-2 text-base font-semibold text-gray-900">Ингредиенты</h3>
                <ul className="space-y-1 text-sm text-gray-800">
                    {recipe.ingredients.map((ing) => (
                        <li
                            key={ing.id}
                            className="flex justify-between border-b border-gray-100 py-1"
                        >
                            <span>{ing.name}</span>
                            <span className="text-gray-600">{ing.amount}</span>
                        </li>
                    ))}
                </ul>
            </section>

            <section>
                <h3 className="mb-2 text-base font-semibold text-gray-900">Шаги</h3>
                <ol className="space-y-3 text-sm text-gray-800">
                    {recipe.steps.map((step, idx) => (
                        <li key={step.id} className="rounded border border-gray-200 bg-white p-3">
                            <div className="font-medium text-gray-900">Шаг {idx + 1}</div>
                            <div className="mt-1 whitespace-pre-wrap text-gray-700">
                                {step.text}
                            </div>
                        </li>
                    ))}
                </ol>
            </section>
        </div>
    );
}

interface ArticleViewProps {
    id: string;
}

function ArticleView({ id }: ArticleViewProps): ReactElement {
    const { data, isLoading, isError } = useGetModerationArticleQuery({ id });

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка статьи...</div>;
    }
    if (isError || !data) {
        return (
            <div className="text-sm text-red-600" data-testid="moderation-detail-error">
                Не удалось загрузить статью
            </div>
        );
    }

    const { article } = data;

    return (
        <div className="space-y-4" data-testid="moderation-article-view">
            <div>
                <h2 className="text-2xl font-semibold text-gray-900">{article.title}</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Автор: {article.authorDisplayName} · Slug: {article.slug}
                    {article.readingTimeMin !== null && ` · ~${article.readingTimeMin} мин чтения`}
                </p>
            </div>

            {article.coverUrl !== null && article.coverUrl !== '' && (
                <img
                    src={article.coverUrl}
                    alt=""
                    className="max-h-80 w-full rounded object-cover"
                />
            )}

            <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm italic text-gray-800">
                {article.excerpt}
            </div>

            <article
                className="markdown-content rounded border border-gray-200 bg-white p-5 text-sm leading-relaxed text-gray-800"
                data-testid="article-markdown-render"
            >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {article.contentMarkdown}
                </ReactMarkdown>
            </article>
        </div>
    );
}

interface CourseViewProps {
    id: string;
}

function CourseView({ id }: CourseViewProps): ReactElement {
    const { data, isLoading, isError } = useGetModerationCourseQuery({ id });
    const [expandedModuleIds, setExpandedModuleIds] = useState<Set<string>>(new Set());

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка курса...</div>;
    }
    if (isError || !data) {
        return (
            <div className="text-sm text-red-600" data-testid="moderation-detail-error">
                Не удалось загрузить курс
            </div>
        );
    }

    const { course } = data;

    const toggleModule = (moduleId: string): void => {
        setExpandedModuleIds((prev) => {
            const next = new Set(prev);
            if (next.has(moduleId)) {
                next.delete(moduleId);
            } else {
                next.add(moduleId);
            }
            return next;
        });
    };

    return (
        <div className="space-y-4" data-testid="moderation-course-view">
            <div>
                <h2 className="text-2xl font-semibold text-gray-900">{course.title}</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Автор: {course.authorDisplayName} · Уровень:{' '}
                    {COURSE_LEVEL_LABEL[course.level]} · Длительность: {course.durationWeeks} нед. ·
                    Уроков: {course.totalLessonsCount} · Цена: {course.priceRub} ₽
                </p>
            </div>

            {course.coverUrl !== null && course.coverUrl !== '' && (
                <img
                    src={course.coverUrl}
                    alt=""
                    className="max-h-80 w-full rounded object-cover"
                />
            )}

            <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm italic text-gray-800">
                {course.excerpt}
            </div>

            <div className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-800">
                {course.description}
            </div>

            <section data-testid="moderation-course-modules">
                <h3 className="mb-2 text-base font-semibold text-gray-900">
                    Модули и уроки ({course.modules.length})
                </h3>
                <div className="space-y-2">
                    {course.modules.map((mod) => {
                        const isOpen = expandedModuleIds.has(mod.id);
                        return (
                            <div
                                key={mod.id}
                                className="rounded border border-gray-200 bg-white"
                                data-testid={`moderation-course-module-${mod.id}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => toggleModule(mod.id)}
                                    data-testid={`moderation-course-module-toggle-${mod.id}`}
                                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-medium text-gray-900 transition hover:bg-gray-50"
                                >
                                    <span className="flex items-center gap-2">
                                        {isOpen ? (
                                            <ChevronDown size={16} />
                                        ) : (
                                            <ChevronRight size={16} />
                                        )}
                                        Неделя {mod.weekNumber}: {mod.title}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {mod.lessons.length} уроков
                                    </span>
                                </button>
                                {isOpen && (
                                    <div
                                        className="border-t border-gray-100 px-3 py-3"
                                        data-testid={`moderation-course-module-body-${mod.id}`}
                                    >
                                        {mod.description !== null && mod.description !== '' && (
                                            <p className="mb-3 text-sm text-gray-700">
                                                {mod.description}
                                            </p>
                                        )}
                                        <ul className="space-y-2 text-sm">
                                            {mod.lessons.map((lesson) => (
                                                <li
                                                    key={lesson.id}
                                                    className="flex items-center justify-between gap-3 rounded border border-gray-100 bg-gray-50 px-3 py-2"
                                                    data-testid={`moderation-course-lesson-${lesson.id}`}
                                                >
                                                    <span className="flex items-center gap-2 text-gray-900">
                                                        {lesson.isPreview && (
                                                            <Eye
                                                                size={14}
                                                                className="text-blue-600"
                                                                aria-label="Превью"
                                                            />
                                                        )}
                                                        {lesson.title}
                                                    </span>
                                                    <span className="text-xs text-gray-600">
                                                        {lesson.durationMin === null
                                                            ? '—'
                                                            : `${lesson.durationMin} мин`}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

interface StreamViewProps {
    id: string;
}

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU');
}

function StreamView({ id }: StreamViewProps): ReactElement {
    const { data, isLoading, isError } = useGetModerationStreamQuery({ id });

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка стрима...</div>;
    }
    if (isError || !data) {
        return (
            <div className="text-sm text-red-600" data-testid="moderation-detail-error">
                Не удалось загрузить стрим
            </div>
        );
    }

    const { stream } = data;

    return (
        <div className="space-y-4" data-testid="moderation-stream-view">
            <div>
                <h2 className="text-2xl font-semibold text-gray-900">{stream.title}</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Автор: {stream.authorDisplayName} · Длительность: {stream.durationMin} мин ·
                    Цена: {stream.priceRub} ₽
                </p>
            </div>

            {stream.coverUrl !== null && stream.coverUrl !== '' && (
                <img
                    src={stream.coverUrl}
                    alt=""
                    className="max-h-80 w-full rounded object-cover"
                />
            )}

            <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                {stream.description}
            </div>

            <dl
                className="grid grid-cols-1 gap-3 rounded border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2"
                data-testid="moderation-stream-fields"
            >
                <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">Запланирован</dt>
                    <dd className="text-gray-900">{formatDateTime(stream.scheduledAt)}</dd>
                </div>
                <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">Длительность</dt>
                    <dd className="text-gray-900">{stream.durationMin} мин</dd>
                </div>
                <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">Цена</dt>
                    <dd className="text-gray-900">{stream.priceRub} ₽</dd>
                </div>
                <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">URL стрима</dt>
                    <dd
                        className="break-all text-gray-900"
                        data-testid="moderation-stream-url"
                    >
                        {stream.streamUrl ?? '—'}
                    </dd>
                </div>
            </dl>
        </div>
    );
}

interface EventViewProps {
    id: string;
}

function EventView({ id }: EventViewProps): ReactElement {
    const { data, isLoading, isError } = useGetModerationEventQuery({ id });

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка мероприятия...</div>;
    }
    if (isError || !data) {
        return (
            <div className="text-sm text-red-600" data-testid="moderation-detail-error">
                Не удалось загрузить мероприятие
            </div>
        );
    }

    const { event } = data;

    return (
        <div className="space-y-4" data-testid="moderation-event-view">
            <div>
                <h2 className="text-2xl font-semibold text-gray-900">{event.title}</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Автор: {event.authorDisplayName} · Цена: {event.priceRub} ₽
                </p>
            </div>

            {event.coverUrl !== null && event.coverUrl !== '' && (
                <img
                    src={event.coverUrl}
                    alt=""
                    className="max-h-80 w-full rounded object-cover"
                />
            )}

            <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                {event.description}
            </div>

            <dl
                className="grid grid-cols-1 gap-3 rounded border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2"
                data-testid="moderation-event-fields"
            >
                <div className="sm:col-span-2">
                    <dt className="text-xs font-medium uppercase text-gray-500">Адрес</dt>
                    <dd className="text-gray-900">{event.address}</dd>
                </div>
                <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">Запланировано</dt>
                    <dd className="text-gray-900">{formatDateTime(event.scheduledAt)}</dd>
                </div>
                <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">Макс. участников</dt>
                    <dd className="text-gray-900">{event.maxParticipants}</dd>
                </div>
            </dl>

            <section data-testid="moderation-event-chefs">
                <h3 className="mb-2 text-base font-semibold text-gray-900">
                    Шефы-ведущие ({event.chefs.length})
                </h3>
                {event.chefs.length === 0 ? (
                    <div className="text-sm text-gray-500">Шефы не указаны</div>
                ) : (
                    <ul className="space-y-2">
                        {event.chefs.map((chef) => (
                            <li
                                key={chef.id}
                                className="flex items-center gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-sm"
                                data-testid={`moderation-event-chef-${chef.id}`}
                            >
                                {chef.avatarUrl !== null && chef.avatarUrl !== '' ? (
                                    <img
                                        src={chef.avatarUrl}
                                        alt=""
                                        className="h-8 w-8 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="h-8 w-8 rounded-full bg-gray-200" />
                                )}
                                <span className="text-gray-900">{chef.displayName}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

export function ModerationDetailPage(): ReactElement {
    const { type, id } = useParams<{ type: ModerationContentType; id: string }>();
    const navigate = useNavigate();

    const [isRejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [actionError, setActionError] = useState<string | null>(null);
    const [reasonError, setReasonError] = useState<string | null>(null);

    const [approveContent, { isLoading: isApproving }] = useApproveContentMutation();
    const [rejectContent, { isLoading: isRejecting }] = useRejectContentMutation();

    if (
        type !== 'recipe' &&
        type !== 'article' &&
        type !== 'course' &&
        type !== 'stream' &&
        type !== 'event'
    ) {
        return (
            <div className="text-sm text-red-600" data-testid="moderation-detail-bad-type">
                Неизвестный тип контента
            </div>
        );
    }

    if (id === undefined || id === '') {
        return (
            <div className="text-sm text-red-600" data-testid="moderation-detail-bad-id">
                Не указан ID
            </div>
        );
    }

    const handleApprove = async (): Promise<void> => {
        setActionError(null);
        try {
            await approveContent({ type, id }).unwrap();
            navigate('/moderation');
        } catch {
            setActionError('Не удалось опубликовать контент');
        }
    };

    const handleRejectSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        const trimmed = rejectReason.trim();
        if (trimmed.length < REASON_MIN_LENGTH) {
            setReasonError(`Минимум ${REASON_MIN_LENGTH} символов`);
            return;
        }
        setReasonError(null);
        try {
            await rejectContent({ type, id, body: { reason: trimmed } }).unwrap();
            setRejectOpen(false);
            setRejectReason('');
            navigate('/moderation');
        } catch {
            setActionError('Не удалось отклонить контент');
        }
    };

    return (
        <div data-testid="moderation-detail-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate('/moderation')}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 transition hover:text-gray-900"
                    data-testid="moderation-detail-back"
                >
                    <ArrowLeft size={16} />
                    К очереди
                </button>
            </div>

            <div className="rounded border border-gray-200 bg-white p-5">
                {type === 'recipe' && <RecipeView id={id} />}
                {type === 'article' && <ArticleView id={id} />}
                {type === 'course' && <CourseView id={id} />}
                {type === 'stream' && <StreamView id={id} />}
                {type === 'event' && <EventView id={id} />}
            </div>

            {actionError !== null && actionError !== '' && (
                <div
                    className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="moderation-detail-action-error"
                >
                    {actionError}
                </div>
            )}

            <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-3">
                <button
                    type="button"
                    onClick={() => {
                        setRejectReason('');
                        setReasonError(null);
                        setRejectOpen(true);
                    }}
                    disabled={isApproving || isRejecting}
                    data-testid="moderation-reject-button"
                    className="inline-flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                >
                    <X size={14} />
                    Отклонить
                </button>
                <button
                    type="button"
                    onClick={handleApprove}
                    disabled={isApproving || isRejecting}
                    data-testid="moderation-approve-button"
                    className="inline-flex items-center gap-2 rounded bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700 disabled:opacity-50"
                >
                    <Check size={14} />
                    Опубликовать
                </button>
            </div>

            <Modal
                isOpen={isRejectOpen}
                onClose={() => setRejectOpen(false)}
                title="Отклонить контент"
                testId="moderation-reject-modal"
            >
                <form
                    onSubmit={handleRejectSubmit}
                    className="space-y-4"
                    data-testid="moderation-reject-form"
                >
                    <Textarea
                        label={`Причина (минимум ${REASON_MIN_LENGTH} символов)`}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={5}
                        error={reasonError ?? undefined}
                        data-testid="moderation-reject-reason"
                        autoFocus
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setRejectOpen(false)}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={isRejecting}
                            data-testid="moderation-reject-submit"
                            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                        >
                            Отклонить
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
